import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { i18n } from '../../translate/i18n';


// Registrar os componentes necessários do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  chartTitle: {
    fontSize: '1.1rem',
    fontWeight: 500,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  chartContainer: {
    position: 'relative',
    height: '300px',
  },
}));

const MetricsCharts = ({ metrics, isAdmin }) => {
  const classes = useStyles();

  if (!metrics) return null;

  const { messageMetrics, performanceMetrics } = metrics;

  // Gráfico de linha: Distribuição horária de mensagens
  const hourlyDistributionData = {
    labels: messageMetrics.hourlyDistribution.map(item => `${item.hour}h`),
    datasets: [
      {
        label: i18n.t('metrics.charts.sentMessages'),
        data: messageMetrics.hourlyDistribution.map(item => item.sent),
        borderColor: 'rgba(36, 180, 165, 1)',
        backgroundColor: 'rgba(36, 180, 165, 0.1)',
        tension: 0.4,
      },
      {
        label: i18n.t('metrics.charts.receivedMessages'),
        data: messageMetrics.hourlyDistribution.map(item => item.received),
        borderColor: 'rgba(143, 237, 194, 1)',
        backgroundColor: 'rgba(143, 237, 194, 0.1)',
        tension: 0.4,
      },
    ],
  };

  const hourlyDistributionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // Gráfico de pizza: Distribuição de leads por status
  const leadsDistributionData = {
    labels: [
      i18n.t('metrics.charts.newLeads'),
      i18n.t('metrics.charts.qualifiedLeads'),
      i18n.t('metrics.charts.convertedLeads'),
    ],
    datasets: [
      {
        data: [
          metrics.leadsMetrics.newLeads,
          metrics.leadsMetrics.qualifiedLeads,
          metrics.conversionMetrics.totalConversions,
        ],
        backgroundColor: ['#24B4A5', '#8FEDC2', '#fdd741'],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  };

  const leadsDistributionOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  // Gráfico de barras: Performance por usuário (apenas admin)
  const userPerformanceData = {
    labels: performanceMetrics.ticketsPerUser.map(user => user.userName),
    datasets: [
      {
        label: i18n.t('metrics.charts.tickets'),
        data: performanceMetrics.ticketsPerUser.map(user => user.ticketCount),
        backgroundColor: '#24B4A5',
        borderColor: '#fff',
        borderWidth: 2,
      },
      {
        label: i18n.t('metrics.charts.conversions'),
        data: performanceMetrics.ticketsPerUser.map(
          user => user.conversionCount
        ),
        backgroundColor: '#8FEDC2',
        borderColor: '#fff',
        borderWidth: 2,
      },
    ],
  };

  const userPerformanceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // Gráfico de barras: Performance por fila (apenas admin)
  const queuePerformanceData = {
    labels: performanceMetrics.queuePerformance.map(queue => queue.queueName),
    datasets: [
      {
        label: i18n.t('metrics.charts.tickets'),
        data: performanceMetrics.queuePerformance.map(
          queue => queue.ticketCount
        ),
        backgroundColor: '#24B4A5',
        borderColor: '#fff',
        borderWidth: 2,
      },
      {
        label: i18n.t('metrics.charts.conversions'),
        data: performanceMetrics.queuePerformance.map(
          queue => queue.conversionCount
        ),
        backgroundColor: '#8FEDC2',
        borderColor: '#fff',
        borderWidth: 2,
      },
    ],
  };

  const queuePerformanceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <Grid container spacing={3}>
      {/* Distribuição Horária de Mensagens */}
      <Grid item xs={12} md={6}>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <Typography className={classes.chartTitle}>
              {i18n.t('metrics.charts.hourlyDistribution')}
            </Typography>
            <div className={classes.chartContainer}>
              <Line
                data={hourlyDistributionData}
                options={hourlyDistributionOptions}
              />
            </div>
          </CardContent>
        </Card>
      </Grid>

      {/* Distribuição de Leads */}
      <Grid item xs={12} md={6}>
        <Card className={classes.card}>
          <CardContent className={classes.cardContent}>
            <Typography className={classes.chartTitle}>
              {i18n.t('metrics.charts.leadsDistribution')}
            </Typography>
            <div className={classes.chartContainer}>
              <Doughnut
                data={leadsDistributionData}
                options={leadsDistributionOptions}
              />
            </div>
          </CardContent>
        </Card>
      </Grid>

      {/* Performance por Usuário (apenas admin) */}
      {isAdmin && (
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.chartTitle}>
                {i18n.t('metrics.charts.userPerformance')}
              </Typography>
              <div className={classes.chartContainer}>
                <Bar
                  data={userPerformanceData}
                  options={userPerformanceOptions}
                />
              </div>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Performance por Fila (apenas admin) */}
      {isAdmin && (
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Typography className={classes.chartTitle}>
                {i18n.t('metrics.charts.queuePerformance')}
              </Typography>
              <div className={classes.chartContainer}>
                <Bar
                  data={queuePerformanceData}
                  options={queuePerformanceOptions}
                />
              </div>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default MetricsCharts;
